/**
 * Approval Package — live status dashboard.
 *
 * Shows quorum progress, each reviewer's state (invited / opened / decided),
 * decision details, and lets compliance officers send reminders, cancel the
 * package, or finalize manually. Polls every 20 seconds so the dashboard
 * stays current while invitations are out.
 */
import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL;

const PKG_STATUS_CFG = {
  draft:     { label: 'Draft',          color: 'bg-slate-100 text-slate-600 border-slate-200' },
  sent:      { label: 'Out for review', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  approved:  { label: 'Approved',       color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:  { label: 'Rejected',       color: 'bg-red-50 text-red-600 border-red-200' },
  expired:   { label: 'Expired',        color: 'bg-slate-100 text-slate-500 border-slate-200' },
  cancelled: { label: 'Cancelled',      color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const DECISION_CFG = {
  approved:                  { label: 'Approved',                  color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  approved_with_conditions:  { label: 'Approved with conditions',  color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400' },
  rejected:                  { label: 'Rejected',                  color: 'bg-red-50 text-red-600 border-red-200', dot: 'bg-red-500' },
  abstained:                 { label: 'Abstained',                 color: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
  recused:                   { label: 'Recused',                   color: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
};

function fmtDate(d) { return d ? new Date(d).toLocaleString() : '—'; }

function recipientState(r) {
  if (r.decision) return 'decided';
  if (r.redeemed_at || r.last_seen_at) return 'opened';
  return 'invited';
}

const STATE_CFG = {
  invited: { label: 'Invited', dot: 'bg-slate-300' },
  opened:  { label: 'Opened',  dot: 'bg-amber-400' },
  decided: { label: 'Decided', dot: 'bg-emerald-500' },
};

export default function PackageDetail({ packageId, onBack }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/ppm/approvals/packages/${packageId}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setDetail(data);
    } catch {}
    setLoading(false);
  }, [packageId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (verb, body = null) => {
    setBusy(verb); setError('');
    try {
      const res = await fetch(`${API}/api/ppm/approvals/packages/${packageId}/${verb}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : null,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || `Failed to ${verb}`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  if (loading || !detail) {
    return <div className="p-8 text-center text-sm text-slate-400">Loading…</div>;
  }

  const pkg = detail.package;
  const cfg = PKG_STATUS_CFG[pkg.status] || PKG_STATUS_CFG.draft;
  const q = detail.quorum;
  const pct = q.threshold === 0 ? 100 : Math.min(100, Math.round((q.approved_count / q.threshold) * 100));

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-700 mb-4 inline-flex items-center gap-1">
          ← Back to SignoffHub
        </button>

        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-slate-800 tracking-tight">{pkg.name}</h2>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${cfg.color}`}>
                {cfg.label}
              </span>
            </div>
            {pkg.description && <p className="text-sm text-slate-500 mt-1">{pkg.description}</p>}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {pkg.status === 'sent' && (
              <>
                <button
                  onClick={() => act('remind')}
                  disabled={!!busy}
                  className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  {busy === 'remind' ? 'Sending…' : 'Send reminder'}
                </button>
                <button
                  onClick={() => act('finalize', { force: false })}
                  disabled={!!busy}
                  className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50"
                >
                  {busy === 'finalize' ? 'Closing…' : 'Finalize'}
                </button>
                <button
                  onClick={() => setConfirmCancel(true)}
                  disabled={!!busy}
                  className="px-3 py-1.5 text-xs rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </>
            )}
            {/* Evidence pack — available the moment a package has been sent.
                Compliance officers can hand auditors a clean URL. */}
            {pkg.status !== 'draft' && (
              <a
                href={`${API}/api/approvals/packages/${packageId}/evidence-pack?format=html`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                title="Open the auditor-grade evidence pack — print to save as PDF."
              >
                📑 Evidence pack
              </a>
            )}
            {pkg.status === 'draft' && (
              <button
                onClick={() => act('send')}
                disabled={!!busy}
                className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold disabled:opacity-50"
              >
                {busy === 'send' ? 'Sending…' : 'Send invitations'}
              </button>
            )}
          </div>
        </div>

        {/* Quorum bar */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-5">
          <div className="flex items-baseline justify-between mb-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Quorum progress</div>
              <div className="text-lg font-bold text-slate-800 mt-0.5">
                {q.approved_count} of {q.threshold} approvals
                {q.remaining > 0 && <span className="text-slate-400 text-sm font-normal"> — {q.remaining} to go</span>}
              </div>
            </div>
            <div className="text-xs text-slate-500">
              Deadline {fmtDate(pkg.deadline_at)}
            </div>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${q.quorum_met ? 'bg-emerald-500' : !q.can_still_reach_quorum ? 'bg-red-400' : 'bg-indigo-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-slate-500">
            <span>👥 {q.total_required} required reviewers</span>
            <span>✅ {q.approved_count} approved</span>
            {q.rejected_count > 0 && <span>❌ {q.rejected_count} rejected</span>}
            {!q.can_still_reach_quorum && pkg.status === 'sent' &&
              <span className="text-red-600 font-semibold">Quorum can no longer be reached</span>}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Materials */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold text-slate-800 mb-3 text-sm">Materials ({detail.items.length})</h3>
            <ul className="divide-y divide-slate-100">
              {detail.items.map((i) => (
                <li key={i.id} className="py-2">
                  <div className="text-sm font-medium text-slate-800 truncate">{i.policy_title}</div>
                  <div className="text-[11px] text-slate-500">
                    {i.policy_number} · v{i.version_number} · effective {i.effective_from ? new Date(i.effective_from).toLocaleDateString() : '—'}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Reviewers */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold text-slate-800 mb-3 text-sm">Reviewers ({detail.recipients.length})</h3>
            <ul className="divide-y divide-slate-100">
              {detail.recipients.map((r) => (
                <ReviewerRow key={r.id} recipient={r} />
              ))}
            </ul>
          </div>
        </div>

        {error && (
          <div className="mt-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">
            {error}
          </div>
        )}

        {confirmCancel && (
          <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
              <h3 className="font-bold text-slate-800 text-lg">Cancel this approval package?</h3>
              <p className="text-sm text-slate-500 mt-2">
                All outstanding invitation links will be revoked immediately and reviewers will no longer be able to submit a decision.
              </p>
              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => setConfirmCancel(false)}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  Keep open
                </button>
                <button
                  onClick={() => { setConfirmCancel(false); act('cancel'); }}
                  className="px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  Cancel package
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ReviewerRow — collapsible, lazy-loads decision intelligence on expand.
// -----------------------------------------------------------------------------
function ReviewerRow({ recipient: r }) {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const st = recipientState(r);
  const stCfg = STATE_CFG[st];
  const dCfg = r.decision ? DECISION_CFG[r.decision] : null;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !stats && !loading) {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/approvals/reviewers/${encodeURIComponent(r.email)}/stats`, { credentials: 'include' });
        const d = await res.json();
        if (d.success) setStats(d.stats);
      } catch {}
      setLoading(false);
    }
  };

  return (
    <li className="py-3">
      <button onClick={toggle} className="w-full text-left">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-800 truncate">
              {r.display_name || r.email}
              {!r.required && <span className="text-[10px] text-slate-400 ml-1">(optional)</span>}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {r.email} · {r.role}
            </div>
          </div>
          <span className="text-[11px] inline-flex items-center gap-1.5 text-slate-600">
            <span className={`w-2 h-2 rounded-full ${dCfg ? dCfg.dot : stCfg.dot}`} />
            {dCfg ? dCfg.label : stCfg.label}
            <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 12 12" fill="currentColor"><path d="M3 4.5L6 7.5L9 4.5"/></svg>
          </span>
        </div>
      </button>
      {r.decision && (
        <div className="mt-2 text-[11px] text-slate-500">
          <div>Signed: <span className="text-slate-700 font-medium">{r.decision_signature}</span></div>
          <div>At: {fmtDate(r.decision_at)} · IP {r.decision_ip || 'unknown'}</div>
          {r.decision_comment && (
            <div className="mt-1 italic text-slate-600">"{r.decision_comment}"</div>
          )}
        </div>
      )}
      {open && (
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3">
          <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-2">
            Reviewer pattern · across all packages
          </div>
          {loading ? (
            <div className="text-xs text-slate-400">Loading…</div>
          ) : stats ? (
            <div className="grid grid-cols-4 gap-2 text-xs">
              <Stat label="Invitations" value={stats.invitations} />
              <Stat label="Decided" value={`${stats.decided}${stats.decision_rate != null ? ` (${stats.decision_rate}%)` : ''}`} />
              <Stat label="Approve rate" value={stats.approval_rate != null ? `${stats.approval_rate}%` : '—'} />
              <Stat label="Avg response" value={stats.avg_response_days != null ? `${stats.avg_response_days} d` : '—'} />
              <div className="col-span-4 mt-1 text-[11px] text-slate-500">
                {stats.counts.approvals} approved · {stats.counts.rejections} rejected · {stats.counts.abstentions} abstained · {stats.counts.recusals} recused
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-400">No history yet.</div>
          )}
        </div>
      )}
    </li>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900">{value ?? '—'}</div>
    </div>
  );
}
